import { Download01 } from '@carespace-icons/general/download01';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import stores, { useTypedDispatch, useTypedSelector } from '@stores/index';
import DownloadResults from '@pages/AiAssistantCustomRomScanResult/DownloadResults';
import { useEffect, useState } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { ICustomRomSession, SelectedUser } from '@stores/interfaces';
import { QrCode01 } from '@carespace-icons/development/qrCode01';
import AdminQRCodeModal from '@pages/AdminQRCodeModal';
import { Mail01 } from '@carespace-icons/communication/mail01';
import SendResultByEmailModal from './SummaryResultReportEmail';
import { sendRomResultInMail, setSelectedRom } from '@stores/rom/customRom';
import { Pencil01 } from '@carespace-icons/editor/pencil01';
import { SummaryResultEditModal } from './SummaryResultEditModal';

interface SummaryResultReportDownloaderProps {
	selectedRom: ICustomRomSession;
	user: SelectedUser;
	fetchSessionData?: (sessionId: string, page: number) => void;
	reportType: 'download' | 'qr' | 'email' | 'edit';
	color?: string;
	completedStatus: boolean
	chartType?: string;
}

export const SummaryResultReportDownloader = ({
	selectedRom,
	user,
	completedStatus,
	fetchSessionData,
	reportType,
	color,
	chartType
}: SummaryResultReportDownloaderProps) => {
	const { t } = useTranslation();
	const { pdfLink = '' } = useTypedSelector(state => state.rom.customRom);
	const [downloadingPhase, setDownLoadingPhase] = useState<string>('');
	const [emailModalOpen, setEmailModalOpen] = useState(false);
	const [editModalOpen, setEditModalOpen] = useState(false);
	const [qrCodeModalOpen, setQrCodeModalOpen] = useState(false);
	const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
	const [loading, setLoading] = useState(false);
	const dispatch = useTypedDispatch();

	useEffect(() => {
		const key = 'downloadProcess';
		if (reportType == 'download')
			if (downloadingPhase.length > 0) {
				message.loading({
					content: downloadingPhase,
					key,
					duration: 0,
				});
			} else {
				message.destroy(key);
				if (loading) {
					message
						.success({
							content: t('Admin.data.survey.done'),
							key,
							duration: 1,
						})
						.then(() => setLoading(false));
				}
			}
	}, [downloadingPhase, loading]);

	const handleQRCodeGeneration = () => {
		setQrCodeModalOpen(true);
		handleDownload();
	};

	const handleEmail = () => {
		setEmailModalOpen(true);
		handleDownload();
	};
	const handleEdit = () => {
		dispatch(setSelectedRom(selectedRom));
		setEditModalOpen(true);
	};

	const handleDownload = async () => {
		setDownLoadingPhase(t('Patient.data.omnirom.fetchingData'));
		setLoading(true);

		const container = document.createElement('div');
		container.style.position = 'absolute';
		container.style.top = '-9999px';
		container.style.left = '-9999px';
		container.style.width = '1500px';
		container.style.zIndex = '-1';
		document.body.appendChild(container);

		const ReportWithProviders = (
			<Provider store={stores}>
				<DownloadResults selectedRom={selectedRom} user={user} chartType={chartType} />
			</Provider>
		);

		const root = createRoot(container);
		root.render(ReportWithProviders);

		await new Promise(res => setTimeout(res, 1000));

		setDownLoadingPhase(t('Patient.data.omnirom.generatingPdf'));

		const pdf = new jsPDF({ unit: 'px', format: 'a3' });
		const pageWidth = pdf.internal.pageSize.getWidth();
		const pageHeight = pdf.internal.pageSize.getHeight();

		const margin = 20;
		let currentY = margin;

		const sections = container.querySelectorAll('.pdf-section');

		for (let i = 0; i < sections.length; i++) {
			const section = sections[i] as HTMLElement;

			const canvas = await html2canvas(section, {
				useCORS: true,
				scale: 1.25,
				scrollY: 0,
				scrollX: 0,
				windowWidth: 1000,
			});

			const imgData = canvas.toDataURL('image/jpeg', 0.8);
			const imgWidth = pageWidth - margin * 2;
			const imgHeight = (canvas.height * imgWidth) / canvas.width;

			if (currentY + imgHeight > pageHeight - margin) {
				pdf.addPage();
				currentY = margin;
			}

			pdf.addImage(imgData, 'PNG', margin, currentY, imgWidth, imgHeight);

			const linkElements = section.querySelectorAll('.pdf-link');

			linkElements.forEach((el, index) => {
				const rect = el.getBoundingClientRect();
				const sectionRect = section.getBoundingClientRect();

				const relativeTop = rect.top - sectionRect.top + 30 + index * 11;
				const relativeLeft = rect.left - sectionRect.left;

				const scale = imgWidth / canvas.width;

				const x = margin + relativeLeft * scale;
				const y = currentY + relativeTop * scale;
				const width = rect.width * scale + 5;
				const height = rect.height * scale + 2;

				const href = el.getAttribute('data-href');
				if (href) {
					pdf.link(x, y, width, height, { url: href });
				}
			});

			currentY += imgHeight;
		}

		if (reportType == 'email') {
			const pdfBlob = pdf.output('blob');
			setPdfBlob(pdfBlob);
		} else if (reportType == 'qr') {
			const pdfBlob = pdf.output('blob');
			const formData = new FormData();
			formData.append('mustSendEmail', 'false');
			formData.append('attachment', pdfBlob);

			await dispatch(
				sendRomResultInMail({
					sessionId: selectedRom?.id,
					formData: formData,
				}),
			);
		} else {
			pdf.save(`report.pdf`);
		}

		root.unmount();
		document.body.removeChild(container);
		setDownLoadingPhase('');
		setLoading(false);
	};

	return reportType === 'email' ? (
		<div>
			<div style={{ cursor: 'pointer' }} onClick={() => handleEmail()}>
				<Mail01 width={20} height={20} color={color ? 'stroke-black' : 'stroke-gray-500'} />
			</div>
			{emailModalOpen && (
				<SendResultByEmailModal
					selectedRom={selectedRom}
					pdfBlob={pdfBlob}
					modalOpen={emailModalOpen}
					setModalOpen={setEmailModalOpen}
					closable={true}
				/>
			)}
		</div>
	) : reportType === 'download' ? (
		<div onClick={handleDownload} style={{ cursor: 'pointer' }}>
			<Download01 width={20} height={20} color={color ? 'stroke-black' : 'stroke-gray-500'} />
		</div>
	) : reportType === 'edit' ? (
		<div>
			<div style={{ cursor: 'pointer' }} onClick={() => handleEdit()}>
				<Pencil01 width={20} height={20} color={color ? 'stroke-black' : 'stroke-gray-500'} />
			</div>
			{editModalOpen && (
				<SummaryResultEditModal
				completedStatus={completedStatus}
				fetchSessionData={fetchSessionData}
					selectedRom={selectedRom}
					modalOpen={editModalOpen}
					setModalOpen={setEditModalOpen}
					closable={true}
				/>
			)}
		</div>
	) : (
		<div>
			<div
				style={{ cursor: 'pointer' }}
				onClick={() => handleQRCodeGeneration()}>
				<QrCode01 width={20} height={20} color={color ? 'stroke-black' : 'stroke-gray-500'} />
			</div>
			{qrCodeModalOpen && (
				<AdminQRCodeModal
					qrCodeModalOpen={qrCodeModalOpen}
					setQrCodeModalOpen={setQrCodeModalOpen}
					qrCodeUrl={pdfLink}
					closable={true}
					loading={loading}
				/>
			)}
		</div>
	);
};
